/**
 * Dealer onboarding (owner, 27 Oct 2026: "whenever a dealer is onbaorded a LOI is made the dealer
 * onboarding is a process like first enquiry then meeting will be done and if everything is final
 * team will send the details to sunny and sunny will approve then the dealer will be automatically
 * added in the system and then only LOI will be generated").
 *
 * Autozone's dealers — the LOI is Bright Autozone's. The ASMs and the office move a prospect from
 * enquiry to meeting to Sunny; Sunny alone approves, which adds the dealer to Autozone's list and
 * makes the Letter of Intent: the owner's own Word letter, its terms fixed, valid three months.
 */
import { isAsmRole } from './domain.js';
import { isCeoDesk } from './ceo.js';

export const DEALER_ONBOARDING_STATUSES = ['ENQUIRY', 'MEETING_DONE', 'SENT_TO_CEO', 'APPROVED', 'DECLINED'] as const;
export type DealerOnboardingStatus = (typeof DEALER_ONBOARDING_STATUSES)[number];

export const DEALER_ONBOARDING_LABELS: Record<DealerOnboardingStatus, string> = {
  ENQUIRY: 'Enquiry',
  MEETING_DONE: 'Meeting done',
  SENT_TO_CEO: 'With Sunny',
  APPROVED: 'Approved',
  DECLINED: 'Declined',
};

/**
 * What the prospect becomes (owner, 29 Oct 2026: an ASM's enquiry "he want to convert into
 * mitr/dealer"): a dealer on Autozone's list with its LOI, or a mitr in the ASM's own book — no
 * LOI. Sunny approves both.
 */
export const ONBOARDING_KINDS = ['DEALER', 'MITRA'] as const;
export type OnboardingKind = (typeof ONBOARDING_KINDS)[number];
export const ONBOARDING_KIND_LABELS: Record<OnboardingKind, string> = { DEALER: 'Dealer', MITRA: 'Mitr' };

/** How long a Letter of Intent stands (owner, 27 Oct 2026: "three months"). */
export const LOI_VALID_MONTHS = 3;

/** The state a prospect is in unless said otherwise — every dealer so far is in Uttar Pradesh. */
export const DEALER_DEFAULT_STATE = 'UTTAR PRADESH';

interface OnboardingPerson {
  email: string;
  roleKey?: string | null;
  permissions?: readonly string[] | null;
  scopePartyId?: string | null;
}

/** Sunny's desk approves (and only it). */
export const approvesDealers = (user: { email: string } | null | undefined): boolean => isCeoDesk(user);

/**
 * Who works the pipeline: the ASMs (their own prospects), and the office — an unscoped sign-in that
 * keeps the dealer list (`parties:write`) — which sees every one. Sunny reads them all.
 */
export function worksDealerOnboarding(user: OnboardingPerson | null | undefined): boolean {
  if (!user) return false;
  if (approvesDealers(user)) return true;
  if (isAsmRole(user.roleKey)) return true;
  return !user.scopePartyId && Boolean(user.permissions?.includes('parties:write'));
}

/** Reads every prospect, not only its own: the office and Sunny. */
export function readsEveryDealerOnboarding(user: OnboardingPerson | null | undefined): boolean {
  if (!user) return false;
  if (approvesDealers(user)) return true;
  return !isAsmRole(user.roleKey) && !user.scopePartyId && Boolean(user.permissions?.includes('parties:write'));
}

export interface DealerOnboardingEventRow {
  kind: string;
  note: string | null;
  by: string | null;
  at: string;
}

export interface DealerOnboardingRow {
  id: string;
  status: DealerOnboardingStatus;
  kind: OnboardingKind;
  /** A mitr's book, by name ("Bright on Wheels", "FAB EV"); null for a dealer. */
  mitrBook: string | null;
  enquiryId: string | null;
  firmName: string;
  proprietorName: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  city: string | null;
  district: string | null;
  pincode: string | null;
  state: string | null;
  gstin: string | null;
  asmId: string | null;
  asmName: string | null;
  notes: string | null;
  meetingAt: string | null;
  meetingPlace: string | null;
  meetingNote: string | null;
  createdBy: string | null;
  createdAt: string;
  sentBy: string | null;
  sentAt: string | null;
  decidedBy: string | null;
  decidedAt: string | null;
  declineNote: string | null;
  partyId: string | null;
  loiDate: string | null;
  loiValidTill: string | null;
  updatedAt: string;
}

export interface DealerOnboardingDetail extends DealerOnboardingRow {
  events: DealerOnboardingEventRow[];
  /** What is still missing before it can go to Sunny, in words; empty when it can go. */
  missingForSunny: string[];
}

export interface DealerOnboardingList {
  rows: DealerOnboardingRow[];
  counts: Record<DealerOnboardingStatus, number>;
}
